<template>
  <Drawer
    id="notification-center"
    :visible="isOpen"
    position="right"
    style="width: 24rem"
    aria-labelledby="notification-center-title"
    :pt="drawerPt"
    @update:visible="close"
  >
    <!-- Header -->
    <template #header>
      <div class="flex flex-1 items-center justify-between gap-2">
        <h2 id="notification-center-title" class="text-lg font-semibold text-white select-none">
          {{ $t('notifications.title') }}
        </h2>
        <Button
          v-if="unreadCount > 0"
          id="btn-mark-all-as-read"
          size="small"
          :label="$t('notifications.markAllAsRead')"
          :disabled="loading"
          @click="markAllRead"
        />
      </div>
    </template>

    <!-- Own close button instead of the Drawer's built-in one, so it keeps its id, a
         Material icon and a tooltip. `autofocus` lets the Drawer focus it on open,
         as it does with the built-in button. -->
    <template #closebutton="{ closeCallback }">
      <Button
        id="btn-close-notification-center"
        severity="secondary"
        text
        rounded
        size="small"
        autofocus
        v-tooltip.bottom="$t('common.close')"
        :aria-label="$t('common.close')"
        @click="closeCallback"
      >
        <template #icon>
          <span class="material-icons select-none" style="font-size: 20px" aria-hidden="true">close</span>
        </template>
      </Button>
    </template>

    <!-- Content (scrolls inside the Drawer's content area) -->
    <!-- Pending Invites Section -->
    <div v-if="workspaceStore.invites.length > 0" class="border-b border-gray-700">
      <div class="px-4 py-3 bg-gray-850 flex items-center justify-between">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-yellow-400">
          {{ $t('invites.notificationSectionTitle') }}
        </h3>
        <Button
          id="btn-view-all-invites"
          variant="link"
          :label="$t('invites.viewAll')"
          :pt="linkButtonPt"
          @click="goToInvites"
        />
      </div>
      <div class="divide-y divide-gray-700">
        <div
          v-for="invite in workspaceStore.invites"
          :key="invite._id"
          class="p-4 border-l-4 border-l-yellow-500 bg-gray-900"
        >
          <p class="font-semibold text-white mb-1">{{ invite.workspaceId?.name }}</p>
          <p class="text-sm text-gray-400 mb-3">
            {{ $t('invites.invitedBy') }}
            <span class="text-gray-300">{{ invite.invitedBy?.name || invite.invitedBy?.email }}</span>
          </p>
          <div class="flex gap-2">
            <Button
              id="btn-accept-invite"
              size="small"
              :label="$t('invites.accept')"
              :disabled="busyInviteId === invite._id"
              @click="onAccept(invite._id)"
            />
            <Button
              id="btn-decline-invite"
              severity="secondary"
              size="small"
              :label="$t('invites.decline')"
              :disabled="busyInviteId === invite._id"
              @click="onDecline(invite._id)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="!loading && notifications.length === 0 && workspaceStore.invites.length === 0"
      class="flex flex-col items-center justify-center h-full text-gray-400 p-4"
    >
      <svg class="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      <p class="text-sm text-center">
        {{ $t('notifications.noNotifications') }}
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading && notifications.length === 0" class="flex justify-center items-center h-full">
      <ProgressSpinner
        style="width: 2rem; height: 2rem"
        stroke-width="4"
        :aria-label="$t('common.loading')"
      />
    </div>

    <!-- Notifications List -->
    <div v-if="notifications.length > 0" class="divide-y divide-gray-700">
      <div
        v-for="notification in notifications"
        :key="notification._id"
        :class="[
          'p-4 hover:bg-gray-800 transition-colors cursor-pointer border-l-4',
          notification.isRead ? 'border-l-gray-700 bg-gray-900' : 'border-l-blue-500 bg-gray-850'
        ]"
      >
        <!-- Notification Priority/Type Indicator -->
        <div class="flex items-start justify-between mb-2">
          <div class="flex items-center gap-2">
            <span
              v-if="!notification.isRead"
              class="inline-block w-2 h-2 bg-blue-500 rounded-full"
            ></span>
            <span
              class="text-xs font-semibold uppercase tracking-wide"
              :class="getPriorityColor(notification.priority)"
            >
              {{ notification.notificationType }}
            </span>
          </div>
          <span class="text-xs text-gray-500">
            {{ formatDate(notification.createdAt) }}
          </span>
        </div>

        <!-- Title -->
        <h3 class="font-semibold text-white mb-1">
          {{ notification.title }}
        </h3>

        <!-- Message -->
        <p class="text-sm text-gray-300 mb-3">
          {{ notification.message }}
        </p>

        <!-- Action Button (if available) -->
        <div v-if="notification.actionUrl" class="mb-3">
          <a
            :href="notification.actionUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-block text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors"
          >
            {{ notification.actionLabel || $t('notifications.seeDetails') }}
          </a>
        </div>

        <!-- Mark as Read Button -->
        <Button
          v-if="!notification.isRead"
          id="btn-mark-as-read"
          variant="link"
          :label="$t('notifications.markAsRead')"
          :pt="linkButtonPt"
          @click="markAsRead(notification._id)"
        />
        <span v-else class="text-xs text-gray-500">
          {{ $t('notifications.read') }}
        </span>
      </div>
    </div>

    <!-- Footer with the number of notifications shown -->
    <template v-if="notifications.length > 0" #footer>
      <p class="text-xs text-gray-400 text-center">
        {{ notifications.length }} {{ $t('notifications.notificationsShown') }}
      </p>
    </template>
  </Drawer>
</template>

<script setup>
/**
 * NotificationCenter.vue
 *
 * Full-height right-hand panel listing the user's pending workspace invites and
 * notifications. Built on PrimeVue Drawer (`position="right"`, 24rem wide, the
 * former `w-96` panel): the Drawer's modal mask replaces the old backdrop, and a
 * mask click, Esc or the close button all emit `close`. The list scrolls inside
 * the Drawer's content area; the footer shows how many notifications are loaded.
 *
 * Invites are read from and accepted/declined through the workspace store;
 * notification state is owned by the parent (NotificationBell) and passed in.
 *
 * @example
 * <NotificationCenter
 *   :is-open="isOpen"
 *   :notifications="notifications"
 *   :loading="loading"
 *   :unread-count="unreadCount"
 *   @close="isOpen = false"
 *   @mark-as-read="markAsRead"
 *   @mark-all-read="markAllAsRead"
 * />
 *
 * @prop {Boolean} isOpen         - Whether the Drawer is shown.
 * @prop {Array}   notifications  - Notifications to list (`_id`, `title`, `message`, `isRead`, ...).
 * @prop {Boolean} loading        - True while notifications are being fetched.
 * @prop {Number}  unreadCount    - Unread notifications; shows "Mark all as read" when > 0.
 *
 * @emits close          - The Drawer was dismissed, or an invite action navigated away.
 * @emits mark-as-read   - (notificationId) A single notification was marked as read.
 * @emits mark-all-read  - "Mark all as read" was clicked.
 * @emits fetch          - (page) Request another page of notifications.
 */
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useWorkspaceStore } from '../stores/workspaceStore';

// `defineProps` and `defineEmits` are compiler macros in <script setup> and
// must not be imported. They are available automatically.

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  notifications: {
    type: Array,
    required: true,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  unreadCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'mark-as-read', 'mark-all-read', 'fetch']);

const { t, locale } = useI18n();
const router = useRouter();
const workspaceStore = useWorkspaceStore();
const busyInviteId = ref(null);

/**
 * Pass-through for the Drawer sections, matching the former DIY panel (gray-800
 * header/footer with gray-700 rules, flush list, thin scrollbar). PrimeVue styles
 * are unlayered (`cssLayer: false`), so Tailwind padding utilities lose to
 * `.p-drawer-header` / `.p-drawer-content` / `.p-drawer-footer`: paddings are set
 * inline. Those classes set no background or border, so Tailwind works for them.
 */
const drawerPt = {
  header: {
    class: 'gap-2 border-b border-[var(--p-surface-700)] bg-[var(--p-surface-800)]',
    style: { padding: '1rem' },
  },
  content: {
    style: {
      padding: 0,
      scrollbarWidth: 'thin',
      scrollbarColor: 'var(--p-surface-700) var(--p-surface-900)',
    },
  },
  footer: {
    class: 'border-t border-[var(--p-surface-700)] bg-[var(--p-surface-800)]',
    style: { padding: '1rem' },
  },
};

/**
 * Pass-through for the inline text links ("View all invites", "Mark as read").
 * Keeps the former compact `text-xs` link look: no Button padding, 0.75rem text.
 * Set inline because PrimeVue's unlayered `.p-button` rules beat Tailwind classes.
 */
const linkButtonPt = { root: { style: { padding: 0, fontSize: '0.75rem' } } };

const close = () => {
  emit('close');
};

const markAsRead = (notificationId) => {
  emit('mark-as-read', notificationId);
};

const markAllRead = () => {
  emit('mark-all-read');
};

const goToInvites = () => {
  emit('close');
  router.push({ name: 'Invites' });
};

const onAccept = async (inviteId) => {
  busyInviteId.value = inviteId;
  try {
    const res = await workspaceStore.acceptInvite(inviteId);
    if (res?.success) {
      router.push({ name: 'Bots', params: { workspaceId: res.data.workspaceId } });
      emit('close');
    }
  } finally {
    busyInviteId.value = null;
  }
};

const onDecline = async (inviteId) => {
  busyInviteId.value = inviteId;
  try {
    await workspaceStore.declineInvite(inviteId);
  } finally {
    busyInviteId.value = null;
  }
};

const getPriorityColor = (priority) => {
  const colors = {
    low: 'text-gray-400',
    normal: 'text-blue-400',
    high: 'text-orange-400',
  };
  return colors[priority] || colors.normal;
};

/**
 * Compact relative time for a notification ("Just now", "5m ago", "3h ago",
 * "2d ago"), localized via `notifications.relativeTime.*`. Older than a week
 * falls back to a short date and time in the active UI locale.
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('notifications.relativeTime.justNow');
  if (diffMins < 60) return t('notifications.relativeTime.minutesAgo', { n: diffMins });
  if (diffHours < 24) return t('notifications.relativeTime.hoursAgo', { n: diffHours });
  if (diffDays < 7) return t('notifications.relativeTime.daysAgo', { n: diffDays });

  // Fallback to full date
  return date.toLocaleDateString(locale.value, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>
